/* An open project's area pages (Resourcing, Roster, Charter, Mappings, and the
   not-built-yet fallback), ported from renderArea (app.ts ~1075). This view is
   mounted when `state.view === "projects"` AND a project is open (`state.area`
   set); the section home with no record open is ProjectsView.

   The area bodies are the most focus- and caret-sensitive surfaces in the
   client (see projectsArea.ts for why), so this view renders the three content
   slots — #banner / #sections / #footnote, exactly as ViewFrame lays them out —
   and hands them to the imperative area controller, which owns them the way the
   vanilla `render(doc)` did: same DOM, same classNames, same three honest
   states, same synchronous focus/caret restoration. React manages the three
   container elements; the controller fills them. The component subscribes to
   the store, so every commit re-runs the controller from a fresh snapshot. */
import { useEffect, useRef, type JSX } from "react";
import { useAppState } from "../shell/store";
import { createProjectArea, type Slots } from "./projectsArea";

export function ProjectAreaView(): JSX.Element {
  /* reading `state` subscribes the component: any commit re-renders it and, via
     the effect below, re-runs the area controller from the new snapshot */
  const { state, store } = useAppState();
  const bannerRef = useRef<HTMLDivElement>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const footnoteRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ReturnType<typeof createProjectArea> | null>(null);

  useEffect(() => {
    if (!bannerRef.current || !sectionsRef.current || !footnoteRef.current) return;
    if (!controllerRef.current) {
      const slots: Slots = {
        banner: bannerRef.current,
        sections: sectionsRef.current,
        footnote: footnoteRef.current,
      };
      controllerRef.current = createProjectArea(slots, store);
    }
    /* one full, synchronous area render per commit — the peer of render(doc);
       area-internal interactions repaint synchronously inside the controller */
    controllerRef.current.render();
    /* `state` is the live snapshot; its identity changes on every commit, so
       this effect re-runs the controller exactly when the vanilla client would
       have re-rendered the area */
  }, [state, store]);

  /* React owns these three containers but renders no children into them, so the
     innerHTML the controller writes is never clobbered on re-render. The ids /
     classes match ViewFrame (and the static index.html) so styles.css targets
     them, and they stay direct children of .wrap. */
  return (
    <>
      <div id="banner" ref={bannerRef} />
      <div id="sections" ref={sectionsRef} />
      <div className="footer-note" id="footnote" ref={footnoteRef} />
    </>
  );
}
